import React, { useEffect, useState } from "react";
import { Modal3 } from "../../../../../components/DIsplay/Modal/Modal3";
import Button1 from "../../../../../components/form/Button1";
import CommonFreeTextInput from "../../../../../components/common/inputs/CommonFreeTextInput";
import CommonDropdownInput from "../../../../../components/common/inputs/CommonDropdownInput";
import CommonDatePicker from "../../../../../components/common/inputs/CommonDatePicker";
import CommonTimeSelectorInput from "../../../../../components/common/inputs/CommonTimeSelectorInput";
import { enqueueSnackbar } from "notistack";
import moment from "moment";

/** Backend cabinClass values */
const CABIN_CLASS_OPTIONS = [
  { code: "ECONOMY", label: "Economy" },
  { code: "PREMIUMECONOMY", label: "Premium Economy" },
  { code: "BUSINESS", label: "Business" },
  { code: "FIRST", label: "First" },
];

const EDITABLE_FIELDS = [
  "aircraftCode",
  "arrivalDate",
  "arrivalTerminal",
  "arrivalTime",
  "bookingClass",
  "cabinClass",
  "departureDate",
  "departureTerminal",
  "departureTime",
  "duration",
  "flightNumber",
];

const FIELD_LABELS = {
  aircraftCode: "Aircraft code",
  arrivalDate: "Arrival date",
  arrivalTerminal: "Arrival terminal",
  arrivalTime: "Arrival time",
  bookingClass: "Booking class",
  cabinClass: "Cabin class",
  departureDate: "Departure date",
  departureTerminal: "Departure terminal",
  departureTime: "Departure time",
  duration: "Duration",
  flightNumber: "Flight number",
};

function normalizeCabinClass(value) {
  if (!value) return "";
  const raw = String(value).replace(/[\s_-]/g, "").toUpperCase();
  if (raw === "ECONOMY") return "ECONOMY";
  if (raw === "PREMIUMECONOMY" || raw === "PREMIUM") return "PREMIUMECONOMY";
  if (raw === "BUSINESS") return "BUSINESS";
  if (raw === "FIRST" || raw === "FIRSTCLASS") return "FIRST";
  return String(value).toUpperCase();
}

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

function toDateOrNull(value) {
  if (!value) return null;
  const parsed = moment(
    value,
    [moment.ISO_8601, "MMM DD YYYY", "MMM D YYYY", "YYYY-MM-DD", "ddd MMM DD"],
    true,
  );
  if (parsed.isValid()) return parsed.toDate();
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function parseDateTime(dateStr, timeStr) {
  if (isBlank(dateStr) || isBlank(timeStr)) return null;
  const datePart = moment(
    dateStr,
    [moment.ISO_8601, "MMM DD YYYY", "MMM D YYYY", "YYYY-MM-DD"],
    true,
  );
  if (!datePart.isValid()) return null;
  const [hh, mm] = String(timeStr).split(":");
  const hours = Number(hh);
  const minutes = Number(mm);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return datePart.clone().hour(hours).minute(minutes).second(0).millisecond(0);
}

/** Matches backend example: "Sep 10 2026" */
function formatDateValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })
    .replace(",", "");
}

function parseDurationInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^\d+(\.\d+)?$/.test(raw)) return raw;
  const match = raw.match(/(\d+(\.\d+)?)/);
  return match ? match[1] : "";
}

function formatDurationForSave(value) {
  const num = Number(String(value || "").replace(/,/g, ""));
  if (!Number.isFinite(num) || num <= 0) return "";
  return `${num} ${num === 1 ? "hour" : "hours"}`;
}

function sanitizeDurationInput(value) {
  return String(value || "")
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*)\./g, "$1");
}

function createEmptyErrors() {
  return EDITABLE_FIELDS.reduce((acc, field) => {
    acc[field] = false;
    return acc;
  }, {});
}

function EditFlightSegmentModal({
  open,
  setOpen,
  segment,
  segmentIndex,
  flightBooking,
  onUpdateTicket,
}) {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState(createEmptyErrors);

  useEffect(() => {
    if (!open || !segment) return;
    const next = {};
    EDITABLE_FIELDS.forEach((field) => {
      if (field === "cabinClass") {
        next[field] = normalizeCabinClass(segment?.[field]);
      } else if (field === "duration") {
        next[field] = parseDurationInput(segment?.[field]);
      } else {
        next[field] = segment?.[field] ?? "";
      }
    });
    setForm(next);
    setErrors(createEmptyErrors());
  }, [open, segment]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateForm = () => {
    const nextErrors = createEmptyErrors();
    let firstMissing = null;

    EDITABLE_FIELDS.forEach((field) => {
      if (isBlank(form[field])) {
        nextErrors[field] = true;
        if (!firstMissing) firstMissing = FIELD_LABELS[field];
      }
    });

    if (firstMissing) {
      setErrors(nextErrors);
      enqueueSnackbar(`${firstMissing} is required.`, { variant: "warning" });
      return false;
    }

    const durationNum = Number(form.duration);
    if (!Number.isFinite(durationNum) || durationNum <= 0) {
      setErrors({ ...nextErrors, duration: true });
      enqueueSnackbar("Duration must be a valid number of hours.", {
        variant: "warning",
      });
      return false;
    }

    const departureAt = parseDateTime(form.departureDate, form.departureTime);
    const arrivalAt = parseDateTime(form.arrivalDate, form.arrivalTime);

    if (!departureAt || !arrivalAt) {
      setErrors({
        ...nextErrors,
        departureDate: !departureAt,
        departureTime: !departureAt,
        arrivalDate: !arrivalAt,
        arrivalTime: !arrivalAt,
      });
      enqueueSnackbar("Please enter valid departure and arrival date/time.", {
        variant: "warning",
      });
      return false;
    }

    if (arrivalAt.isBefore(departureAt)) {
      setErrors({
        ...nextErrors,
        arrivalDate: true,
        arrivalTime: true,
        departureDate: true,
        departureTime: true,
      });
      enqueueSnackbar("Arrival date/time cannot be before departure.", {
        variant: "warning",
      });
      return false;
    }

    setErrors(createEmptyErrors());
    return true;
  };

  const handleSave = () => {
    if (segmentIndex == null || segmentIndex < 0) {
      enqueueSnackbar("Unable to update this segment.", { variant: "error" });
      return;
    }
    if (!validateForm()) return;

    const existing = flightBooking?.flights?.[segmentIndex] || {};
    const { __index, ...safeExisting } = existing;

    const flights = [...(flightBooking?.flights || [])];
    flights[segmentIndex] = {
      ...safeExisting,
      aircraftCode: form.aircraftCode || "",
      flightNumber: String(form.flightNumber || ""),
      cabinClass: normalizeCabinClass(form.cabinClass),
      bookingClass: String(form.bookingClass || "").toUpperCase(),
      departureDate: form.departureDate || "",
      departureTime: form.departureTime || "",
      departureTerminal: form.departureTerminal || "",
      arrivalDate: form.arrivalDate || "",
      arrivalTime: form.arrivalTime || "",
      arrivalTerminal: form.arrivalTerminal || "",
      duration: formatDurationForSave(form.duration),
    };

    onUpdateTicket?.({
      ...flightBooking,
      flights,
    });
    setOpen(false);
  };

  return (
    <Modal3 open={!!open} setOpen={setOpen} title="Edit flight segment">
      <div className="p-4 sm:p-6 flex flex-col gap-4 min-w-[280px] sm:min-w-[520px] max-w-[640px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <CommonFreeTextInput
            label="Flight number"
            value={form.flightNumber || ""}
            placeholder="e.g. 556"
            error={errors.flightNumber}
            onChange={(value) =>
              updateField("flightNumber", String(value || "").replace(/\D/g, ""))
            }
          />
          <CommonFreeTextInput
            label="Aircraft code"
            value={form.aircraftCode || ""}
            placeholder="e.g. 73H"
            error={errors.aircraftCode}
            onChange={(value) => updateField("aircraftCode", value)}
          />
          <CommonDropdownInput
            label="Cabin class"
            options={CABIN_CLASS_OPTIONS}
            optionObject
            value={form.cabinClass || ""}
            placeholder="Select cabin"
            error={errors.cabinClass}
            onChange={(value) =>
              updateField("cabinClass", normalizeCabinClass(value))
            }
          />
          <CommonFreeTextInput
            label="Booking class"
            value={form.bookingClass || ""}
            placeholder="e.g. R"
            error={errors.bookingClass}
            onChange={(value) => {
              const letters = String(value || "").match(/[a-z]/gi) || [];
              updateField(
                "bookingClass",
                letters.length ? letters[letters.length - 1].toUpperCase() : "",
              );
            }}
          />
          <CommonDatePicker
            label="Departure date"
            value={toDateOrNull(form.departureDate)}
            placeholder="Select date"
            error={errors.departureDate}
            onChange={(date) =>
              updateField("departureDate", formatDateValue(date))
            }
          />
          <CommonTimeSelectorInput
            label="Departure time"
            value={form.departureTime || ""}
            placeholder="HH:MM"
            error={errors.departureTime}
            onChange={(value) => updateField("departureTime", value)}
          />
          <CommonFreeTextInput
            label="Departure terminal"
            value={form.departureTerminal || ""}
            placeholder="e.g. 2I"
            error={errors.departureTerminal}
            onChange={(value) => updateField("departureTerminal", value)}
          />
          <CommonDatePicker
            label="Arrival date"
            value={toDateOrNull(form.arrivalDate)}
            placeholder="Select date"
            error={errors.arrivalDate}
            onChange={(date) => updateField("arrivalDate", formatDateValue(date))}
          />
          <CommonTimeSelectorInput
            label="Arrival time"
            value={form.arrivalTime || ""}
            placeholder="HH:MM"
            error={errors.arrivalTime}
            onChange={(value) => updateField("arrivalTime", value)}
          />
          <CommonFreeTextInput
            label="Arrival terminal"
            value={form.arrivalTerminal || ""}
            placeholder="e.g. 2"
            error={errors.arrivalTerminal}
            onChange={(value) => updateField("arrivalTerminal", value)}
          />
          <CommonFreeTextInput
            label="Duration (hours)"
            value={form.duration || ""}
            placeholder="e.g. 5"
            error={errors.duration}
            onChange={(value) =>
              updateField("duration", sanitizeDurationInput(value))
            }
          />
        </div>

        <div className="flex gap-3 pt-1">
          <Button1
            variant="outlined"
            className="!border-primary !text-primary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button1>
          <Button1 onClick={handleSave}>Save</Button1>
        </div>
      </div>
    </Modal3>
  );
}

export default EditFlightSegmentModal;
